import PlayListModal from "@components/PlayListModal";
import TrackSearchModal from "@components/RecommendationModal";
import useSpotifyTracks, { Track } from "@hooks/useSpotifyTracks";
import AddIcon from "@mui/icons-material/Add";
import BoltIcon from "@mui/icons-material/Bolt";
import SearchIcon from "@mui/icons-material/SavedSearch";
import { Box, IconButton, Toolbar, Tooltip } from "@mui/material";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { useTrackRecommendations } from "../../context/TrackRecommendationsContext";

interface CustomToolbarProps {
  setSyncEnabled: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface TracksMap {
  tracks: Track[][];
  songId?: string;
}

const LyricToolbar: React.FC<CustomToolbarProps> = ({ setSyncEnabled }: CustomToolbarProps) => {
  const { switchTrack, queueTrack } = useSpotifyTracks();
  const [liveModalOpen, setLiveModalOpen] = useState(false);
  const [isPlaylistModalOpen, setIsPlayListModalOpen] = useState(false);
  const [tracksMap, setTracksMap] = useState<TracksMap>({ tracks: [] });

  // Get track recommendations to show live track indicator
  const trackRecommendations = useTrackRecommendations();

  const isAnyModalOpen = liveModalOpen || isPlaylistModalOpen;

  const toggleSearchLive = () => {
    setLiveModalOpen(s => !s);
    setSyncEnabled(s => !s);
  };

  const addToFavorites = () => {
    setIsPlayListModalOpen(true);
    setSyncEnabled(false);
  };

  const handleCloseModal = () => {
    setIsPlayListModalOpen(false);
    setSyncEnabled(true);
  };

  const handleTrackSelect = (trackId: string) => {
    switchTrack(trackId);
    setLiveModalOpen(false);
    setSyncEnabled(true);
  };

  const handleTrackQueue = async (trackId: string, trackName: string) => {
    const success = await queueTrack(trackId);
    if (success) {
      toast.success(`Added "${trackName}" to queue`, {
        position: "bottom-center",
        autoClose: 2500
      });
    } else {
      toast.error(`Failed to add "${trackName}" to queue`, {
        position: "bottom-center",
        autoClose: 3000
      });
    }
  };

  const closeLiveModal = () => {
    setLiveModalOpen(false);
    setSyncEnabled(true);
  };

  return (
    <Toolbar>
      {liveModalOpen && (
        <TrackSearchModal
          open={liveModalOpen}
          onClose={closeLiveModal}
          onTrackSelect={handleTrackSelect}
          onTrackQueue={handleTrackQueue}
          tracksMap={tracksMap}
          setTracksMap={setTracksMap}
        />
      )}
      {!isAnyModalOpen && (
        <>
          <Box sx={{ position: "relative" }}>
            <IconButton onClick={toggleSearchLive}>
              <SearchIcon fontSize="large" />
            </IconButton>

            {/* Live track indicator */}
            {trackRecommendations.hasLiveTracks && (
              <Box
                sx={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  backgroundColor: "warning.main",
                  borderRadius: "50%",
                  width: 20,
                  height: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1
                }}
              >
                <Tooltip title="Live version available" placement="top">
                  <BoltIcon fontSize="small" sx={{ color: "white" }} />
                </Tooltip>
              </Box>
            )}
          </Box>
          <IconButton onClick={addToFavorites}>
            <AddIcon fontSize="large" />
          </IconButton>
        </>
      )}
      <PlayListModal isOpen={isPlaylistModalOpen} onClose={handleCloseModal} />
    </Toolbar>
  );
};

export default LyricToolbar;
